import type { ComponentProps, MouseEvent, ReactElement } from 'react';
import { useRender } from '@base-ui/react/use-render';
import { createLink } from '@tanstack/react-router';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DEFAULT_VIEW_LABEL = 'View';
const ROW_VIEW_CLASS = buttonVariants({ variant: 'outline', size: 'sm' });

const stopRowClick = (event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation();

interface RowViewAnchorProps extends ComponentProps<'a'> {
  label?: string | undefined;
}

function RowViewAnchor({
  className,
  onClick,
  label = DEFAULT_VIEW_LABEL,
  ...props
}: RowViewAnchorProps) {
  return (
    <a
      {...props}
      className={cn(ROW_VIEW_CLASS, className)}
      onClick={(event) => {
        stopRowClick(event);
        onClick?.(event);
      }}
    >
      {label}
    </a>
  );
}

export const RowViewLink = createLink(RowViewAnchor);

interface RowViewRenderProps {
  render: ReactElement;
  ariaLabel: string;
  label?: string | undefined;
}

export function RowViewRender({
  render,
  ariaLabel,
  label = DEFAULT_VIEW_LABEL,
}: RowViewRenderProps) {
  return useRender({
    defaultTagName: 'a',
    render,
    props: {
      'aria-label': ariaLabel,
      className: ROW_VIEW_CLASS,
      onClick: stopRowClick,
      children: label,
    },
  });
}
